import type { Auth, User } from "firebase/auth";
import { type FormEvent, useEffect, useMemo, useRef, useState } from "react";
import * as api from "./api";
import { matchesQuery } from "./filter";
import { checkingText, type CompactAccount, isNetworkError, offlineText, offlineWindowHours, unlessSaved } from "./offline/cache";
import { userScope } from "./offline/db";
import { loadEntry, localPhotos, withLocalLoads } from "./offline/local";
import { enqueue, NO_STORAGE, type OutboxEntry, STATE_TEXT, waiting } from "./offline/outbox";
import { CONFIRM_OLD, keepVisit } from "./offline/visitQueue";
import { CHANGE_HINT, CHANGE_LABEL, canChange, type RefusedField } from "./offline/messages";
import { DRAFT_ASK, DRAFT_KEEP, KEPT_FORM, type KeptForm, keptForm, moreRefusedText, refusedText, refusedVisits, reopenView, visitDraft } from "./visitFix";
import { useHoldReload } from "./offline/hold";
import { accountList, keptAccount, keptVisitDefaults, loadVisit, nearbyList, type VisitData, visitLabelList } from "./offline/pages";
import { InstallNote } from "./offline/Install";
import { useOutbox } from "./offline/useOutbox";
import { go, useTitle } from "./hooks";
import { GallonsFields, NoScrapConfirm } from "./GallonsFields";
import { PhotoAdd } from "./PhotoPages";
import { PlaceConfirm } from "./PlaceConfirm";
import { bucket, decidePlace, FAR_M, isPhone, needsCheck, readFix, stopPin, takeHint } from "./place";
import { asksNoScrap, capacityKey, capacityWarning, truckWarning } from "./pickup";
import { saveFailure } from "./save";
import { ensureLoad } from "./today";
import { type Failure, failureOf, Problem } from "./UserPages";
import { VisitLabelPicker, VisitNote } from "./VisitLabels";
import {
  afterSave,
  asksLeft,
  type Attempt,
  attemptEntry,
  blankForm,
  driverChoices,
  entryForm,
  firstAttempt,
  formEntry,
  isOldEntry,
  localNow,
  oldEntryText,
  NOTHING_REASONS,
  REASON_TEXT,
  truckChoices,
  type VisitForm,
  volumeOf,
  volumeText,
  zonedIso,
} from "./visits";
import { Skeleton } from "./Skeleton";

interface Target {
  id: string;
  number: string;
  name: string;
  address?: string;
  routeStopId?: string | null;
  distance_m?: number;
}

type Loads = { list: api.Load[]; canMake: boolean; day: string };
type Note = { ok: boolean; text: string; session?: boolean; entry?: string } | null;

const SEARCH_MAX = 8;
const newId = () => crypto.randomUUID();

const distanceText = (metres: number) => (metres < 1000 ? `${Math.round(metres)} m` : `${(metres / 1000).toFixed(1)} km`);

const KEPT_FORM_BACK = "The app updated to a new version. Your typed values are back; check them, then save.";

/** The server refuses a visit at a stop with this text until the route has its check. */
const PRE_TRIP = "Do the pre-trip check on Route first.";

/** Record one tank visit at the tank. A driver types only the gallons; the rest has defaults. */
export function VisitPage({
  slug,
  base,
  org,
  auth,
  user,
  canView,
}: {
  slug: string;
  base: string;
  org: api.Bootstrap;
  auth: Auth;
  user: User;
  canView: boolean | undefined;
}) {
  const [defaults, setDefaults] = useState<api.VisitDefaults | Failure | undefined>(undefined);
  const [labelList, setLabelList] = useState<api.VisitLabels | null>(null);
  const [loads, setLoads] = useState<Loads>({ list: [], canMake: false, day: "" });
  const loadId = useRef<string | null>(null);
  const saves = useRef(0);
  const [attempt, setAttempt] = useState(0);
  const [target, setTarget] = useState<Target | null>(null);
  const [nearby, setNearby] = useState<api.NearbyAccount[]>([]);
  const [finding, setFinding] = useState<"" | "stop" | "gps" | "search">("");
  const [query, setQuery] = useState("");
  const [accounts, setAccounts] = useState<CompactAccount[] | null>(null);
  const scope = useMemo(() => userScope(slug, user.uid), [slug, user.uid]);
  const token = () => user.getIdToken();
  const queued = useOutbox(scope).all;
  const [offlineAt, setOfflineAt] = useState<number | null>(null);
  const [checking, setChecking] = useState(false);
  const [form, setForm] = useState<VisitForm>(() => {
    const now = localNow(new Date(), org.time_zone);
    return blankForm(now.day, now.time);
  });
  const [timeSet, setTimeSet] = useState(false);
  const [error, setError] = useState<{ field: RefusedField | "save" | "note"; text: string } | null>(null);
  const [note, setNote] = useState<Note>(null);
  const [busy, setBusy] = useState(false);
  const tries = useRef<Attempt>(firstAttempt(newId));
  const live = useRef(true);
  const gallonsRef = useRef<HTMLInputElement>(null);
  const scrapOk = useRef(false);
  const overCapacity = useRef<string | null>(null);
  const [askNoScrap, setAskNoScrap] = useState(false);
  const saveRef = useRef<HTMLButtonElement>(null);
  const [askOld, setAskOld] = useState(false);
  const [askPlace, setAskPlace] = useState<{ place: api.Place; confirmed: boolean; hint: boolean } | null>(null);
  const [fixing, setFixing] = useState<string | null>(null);
  const [askDraft, setAskDraft] = useState<OutboxEntry | null>(null);
  const toldRefused = useRef<string | null>(null);
  const fixId = useRef(new URLSearchParams(location.search).get("fix"));
  useTitle(`Record a visit · ${org.name}`);
  useEffect(() => {
    live.current = true;
    return () => {
      live.current = false;
    };
  }, []);

  const loaded = typeof defaults === "object" ? defaults : null;
  const volume = volumeOf(loaded?.unit);
  const hours = loaded?.offline_hours ?? offlineWindowHours();

  const chooseNearest = () => {
    if (!("geolocation" in navigator)) {
      setFinding("search");
      return;
    }
    setFinding("gps");
    navigator.geolocation.getCurrentPosition(
      async (fix) => {
        try {
          const found = await nearbyList(scope, token, { lat: fix.coords.latitude, lng: fix.coords.longitude });
          if (!live.current) return;
          setNearby(found);
          const first = found[0];
          setTarget(first ? { ...first, routeStopId: null } : null);
          setFinding(first ? "" : "search");
        } catch {
          if (live.current) setFinding("search");
        }
      },
      () => live.current && setFinding("search"),
      { enableHighAccuracy: true, timeout: 15_000, maximumAge: 60_000 },
    );
  };

  const startFrom = async (found: api.VisitDefaults, after?: string) => {
    const stop = found.next_stop;
    if (!stop || stop.account_id === after) {
      chooseNearest();
      return;
    }
    setFinding("stop");
    try {
      const account = await api.account(await user.getIdToken(), stop.account_id).catch(async (e) => {
        if (!isNetworkError(e)) throw e;
        return (await keptAccount(scope, stop.account_id)) ?? Promise.reject(e);
      });
      if (!live.current) return;
      setTarget({ id: account.id, number: account.number, name: account.name, address: account.address, routeStopId: stop.route_stop_id });
      setFinding("");
    } catch {
      if (live.current) chooseNearest();
    }
  };

  const restoreOrStart = async (found: api.VisitDefaults) => {
    const kept = keptForm(await scope.read<KeptForm<Target> | null>(KEPT_FORM).then((row) => row?.value, () => null), Date.now());
    if (kept) void scope.write(KEPT_FORM, null);
    if (!live.current) return;
    if (!kept) return startFrom(found);
    console.info(`visit form: the form that the update kept is back (account ${kept.target.number})`);
    setTarget(kept.target);
    setForm(kept.form);
    setTimeSet(kept.timeSet);
    setNote({ ok: true, text: KEPT_FORM_BACK });
  };
  useHoldReload(target !== null && visitDraft(form) && !busy, "the visit form", () => scope.write(KEPT_FORM, { target, form, timeSet, at: Date.now() } satisfies KeptForm<Target | null>));

  useEffect(() => {
    if (canView !== true) return;
    let current = true;
    setDefaults(undefined);
    const day = localNow(new Date(), org.time_zone).day;
    const show = ({ defaults: found, loads: listed, at, fresh }: VisitData, first: boolean) => {
      if (!current) return;
      setOfflineAt(at);
      setChecking(Boolean(fresh));
      setDefaults(found);
      setLoads({ list: listed?.loads ?? [], canMake: Boolean(listed?.can_make), day });
      if (fresh)
        unlessSaved(fresh, saves).then(
          (data) => {
            if (data) return show(data, false);
            if (!current) return;
            setChecking(false);
            setOfflineAt(null);
          },
          (e) => current && (isNetworkError(e) ? setChecking(false) : setDefaults(failureOf(e))),
        );
      // A late answer must not change the driver's picks.
      if (!first) return;
      setForm((before) => ({ ...before, driverId: found.driver_id ?? "", truckId: found.truck_id ?? "" }));
      if (!fixId.current) void restoreOrStart(found);
    };
    loadVisit(scope, () => user.getIdToken(), day)
      .then((data) => show(data, true))
      .catch((e) => current && setDefaults(failureOf(e)));
    return () => {
      current = false;
    };
  }, [user, attempt, canView, scope, org.time_zone]);

  useEffect(() => {
    if (canView !== true) return;
    let current = true;
    const started = performance.now();
    void visitLabelList(scope, () => user.getIdToken()).then((found) => {
      if (!current) return;
      setLabelList(found);
      console.info(`visit form: ${found ? `${found.labels.filter((label) => label.active).length} labels` : "no labels, only the note"} in ${Math.round(performance.now() - started)} ms`);
    });
    return () => {
      current = false;
    };
  }, [user, attempt, canView, scope]);

  const keepValues = () => {
    setAskDraft(null);
    requestAnimationFrame(() => document.getElementById("visit-fix")?.focus());
  };

  /** Put a refused visit back in the form and mark the field that the server named, so that the driver changes it and saves it again. */
  const openFix = async (entry: OutboxEntry, replace = false) => {
    if (!replace && !fixing && visitDraft(form)) {
      console.info(`visit form: ${entry.kind} ${entry.id} waits, the form has values that are not saved`);
      setAskDraft(entry);
      requestAnimationFrame(() => document.getElementById("visit-draft-keep")?.focus());
      return;
    }
    setAskDraft(null);
    const body = entry.body as unknown as api.VisitEntry;
    const view = reopenView(entry, entryForm(body, org.time_zone, volume), volume.word);
    setForm(view.form);
    setTimeSet(true);
    setAskOld(false);
    setFixing(entry.id);
    setNote({ ok: false, text: view.note });
    setError(view.error);
    console.info(`visit form: refused ${entry.kind} ${entry.id} is in the form, field ${view.error?.field ?? "none"}`);
    tries.current = firstAttempt(newId);
    const account = (await keptAccount(scope, body.account_id)) ?? (await user.getIdToken().then((t) => api.account(t, body.account_id)).catch(() => null));
    if (!live.current) return;
    setFinding("");
    setTarget({ id: body.account_id, number: account?.number ?? "", name: account?.name ?? entry.label, address: account?.address, routeStopId: body.route_stop_id ?? null });
    const focusId = view.focusId;
    if (focusId) requestAnimationFrame(() => document.getElementById(focusId)?.focus());
  };
  useEffect(() => {
    const id = fixId.current;
    if (!id || !loaded || !queued) return;
    fixId.current = null;
    const entry = queued.find((found) => found.id === id && found.kind === "visit" && found.state === "failed");
    if (entry) void openFix(entry);
    else chooseNearest();
  }, [loaded, queued]);

  useEffect(() => {
    const waits = refusedVisits(queued, [fixing]);
    if (!waits || toldRefused.current === waits.newest.id) return;
    toldRefused.current = waits.newest.id;
    console.info(`visit form: ${waits.count} refused visits wait in the outbox, newest visit ${waits.newest.id}, field ${waits.newest.field ?? "none"}`);
  }, [queued, fixing]);

  const searchAccounts = async () => {
    setFinding("search");
    if (accounts) return;
    try {
      const list = await accountList(scope, token);
      if (live.current) setAccounts(list);
    } catch {
      if (live.current) setAccounts([]);
    }
  };

  if (canView === false || defaults === "hidden") {
    return (
      <main data-testid="visit-hidden">
        <h1 tabIndex={-1}>Record a visit</h1>
        <p>Your role does not record visits.</p>
      </main>
    );
  }

  const change = (fields: Partial<VisitForm>) => {
    setForm((before) => ({ ...before, ...fields }));
    setError(null);
    scrapOk.current = false;
    setAskNoScrap(false);
    setAskPlace(null);
  };
  const pick = (next: Target) => {
    setTarget(next);
    setAskPlace(null);
    if (error?.field === "account") setError(null);
    setFinding("");
    setQuery("");
    tries.current = firstAttempt(newId);
    requestAnimationFrame(() => gallonsRef.current?.focus());
  };

  /** The pin of the account: the kept account list first, so it works with no signal. */
  const pinOf = async (at: Target): Promise<api.LatLng | null> => {
    const kept = await keptAccount(scope, at.id);
    if (kept) return kept.pin ?? null;
    try {
      const account = await api.account(await user.getIdToken(), at.id);
      return stopPin(account.tank, account.door);
    } catch {
      return null;
    }
  };
  /** The container size of the account: the kept account list first, so it works with no signal. 0: unknown. */
  const capacityOf = async (at: Target): Promise<number> => {
    const kept = await keptAccount(scope, at.id);
    if (kept?.capacity_gal !== undefined) return kept.capacity_gal;
    try {
      return (await api.account(await user.getIdToken(), at.id)).capacity_gal;
    } catch {
      console.info(`visit: account ${at.id} has no container size here, no capacity check`);
      return 0;
    }
  };
  /** Rule D-19: the place of a visit on a phone. Null: the driver must answer the question first. */
  const visitPlace = async (at: Target, confirmed: boolean): Promise<{ place: api.Place | null } | null> => {
    const phone = isPhone();
    const on = loaded?.place_check_on !== false;
    const pin = phone && on ? await pinOf(at) : null;
    if (!needsCheck(phone, pin, on)) {
      console.info(`visit: account ${at.id} has no place check, ${!on ? "the check is off" : phone ? "the account has no pin" : "not a phone"}`);
      return { place: null };
    }
    const started = performance.now();
    const decision = decidePlace(phone, pin, await readFix(), loaded?.place_far_m ?? FAR_M);
    const check = decision.place?.check ?? "none";
    console.info(`visit: account ${at.id} place check ${check}, distance ${bucket(decision.place?.distance_m)}, ${Math.round(performance.now() - started)} ms`);
    if (!decision.ask) return { place: decision.place };
    if (!live.current) return null;
    setAskPlace({ place: decision.place, confirmed, hint: decision.ask === "denied" && takeHint() });
    requestAnimationFrame(() => document.getElementById("place-yes-visit")?.focus());
    return null;
  };
  const placeYes = (ask: { place: api.Place; confirmed: boolean }) => {
    setAskPlace(null);
    console.info(`visit: place ${ask.place.check} confirmed by the driver`);
    void save(undefined, ask.confirmed, ask.place);
  };
  const placeNo = (ask: { place: api.Place }) => {
    setAskPlace(null);
    console.info(`visit: place ${ask.place.check}, the driver cancelled, nothing is saved`);
    saveRef.current?.focus();
  };

  const save = async (event?: FormEvent, confirmed = false, place?: api.Place | null) => {
    event?.preventDefault();
    if (!target || busy || !loaded) return;
    const now = localNow(new Date(), org.time_zone);
    const used = timeSet ? form : { ...form, day: now.day, time: now.time };
    const built = formEntry(used, { accountId: target.id, routeStopId: target.routeStopId }, org.time_zone, volume);
    if ("error" in built) {
      setError({ field: built.field, text: built.error });
      requestAnimationFrame(() => document.getElementById(`visit-${built.field}`)?.focus());
      return;
    }
    if (built.kind === "pickup" && asksNoScrap(built.raw_gal, built.scrap_gal) && !scrapOk.current) {
      setAskNoScrap(true);
      requestAnimationFrame(() => document.getElementById("visit-no-scrap-yes")?.focus());
      return;
    }
    setAskNoScrap(false);
    if (built.kind === "pickup" && built.raw_gal != null) {
      const gallons = { raw: built.raw_gal, scrap: built.scrap_gal ?? 0 };
      const key = `${target.id} ${capacityKey(gallons)}`;
      const capacity = overCapacity.current === key ? null : await capacityOf(target);
      if (!live.current) return;
      const truck = loaded.trucks?.find((t) => t.id === used.truckId);
      const warning = capacity === null ? "" : capacity > 0 ? capacityWarning(gallons, capacity) : truckWarning(gallons, truck);
      if (warning) {
        console.info(`visit: account ${target.id} gallons are over the ${capacity ? "container" : `truck ${truck?.code}`} size, the driver must save again`);
        overCapacity.current = key;
        setError({ field: "gallons", text: warning });
        requestAnimationFrame(() => gallonsRef.current?.focus());
        return;
      }
    }
    if (!confirmed && isOldEntry(built.at, new Date(), hours)) {
      setAskOld(true);
      requestAnimationFrame(() => document.getElementById("visit-old-yes")?.focus());
      return;
    }
    setAskOld(false);
    setAskPlace(null);
    let where = place;
    if (where === undefined) {
      setBusy(true);
      const found = await visitPlace(target, confirmed);
      if (!live.current) return;
      setBusy(false);
      if (!found) return;
      where = found.place;
    }
    const placed = where ? { ...built, place: where } : built;
    scrapOk.current = false;
    setBusy(true);
    let load: api.Load | undefined;
    try {
      load = await pickupLoad(placed);
    } catch (e) {
      if (!live.current) return;
      setError({ field: "save", text: e instanceof Error && e.message === NO_STORAGE ? NO_STORAGE : saveFailure(e, "The visit").text });
      setBusy(false);
      return;
    }
    await send(attemptEntry(tries.current, load ? { ...placed, load_id: load.id } : placed), confirmed);
  };
  const noScrapYes = () => {
    scrapOk.current = true;
    void save();
  };
  const changeScrap = () => {
    setAskNoScrap(false);
    requestAnimationFrame(() => document.getElementById("visit-scrap")?.focus());
  };
  const pickWhen = (set: boolean) => {
    const now = localNow(new Date(), org.time_zone);
    setTimeSet(set);
    change({ day: now.day, time: now.time });
    console.info(`visit form: time is ${set ? "set by the driver" : "now"}`);
    if (set) requestAnimationFrame(() => document.getElementById("visit-day")?.focus());
  };
  const changeOldDate = () => {
    setAskOld(false);
    requestAnimationFrame(() => document.getElementById("visit-day")?.focus());
  };

  /** Rule L-2: as on Route, a pickup of the user goes on the open load of the truck, after the pre-trip check. */
  const pickupLoad = async (built: Omit<api.VisitEntry, "id">) => {
    const truck = trucks.find((t) => t.id === built.truck_id)?.code;
    const day = localNow(new Date(built.at), org.time_zone).day;
    const mine = !built.driver_id || built.driver_id === loaded?.driver_id;
    if (built.kind !== "pickup" || !truck || !mine || day !== loads.day || (loaded?.pre_trip_needed && !startWaits)) return undefined;
    return ensureLoad(withLocalLoads(loads.list, queued ?? []), loads.canMake, truck, day, async (entry) => {
      loadId.current ??= crypto.randomUUID();
      const kept = await enqueue(scope, loadEntry({ id: loadId.current, ...entry }, `New load, truck ${truck}`));
      loadId.current = null;
      saves.current += 1;
      return withLocalLoads(loads.list, [kept]).find((load) => load.id === kept.id) ?? null;
    });
  };

  const send = async (entry: api.VisitEntry, confirmed: boolean) => {
    setBusy(true);
    setNote(null);
    setError(null);
    setAskDraft(null);
    try {
      const what =
        entry.kind === "pickup" ? `${volumeText(entry.raw_gal ?? 0, volume)} at ${target?.number}` : `${REASON_TEXT[entry.reason ?? "other"]} at ${target?.number}`;
      const kept = await keepVisit(scope, entry, what, { now: new Date(), hours, confirmed, fixes: fixing });
      if (kept === CONFIRM_OLD) {
        setAskOld(true);
        return;
      }
      saves.current += 1;
      if (fixing) history.replaceState(history.state, "", location.pathname);
      setFixing(null);
      if (!live.current) return;
      tries.current = afterSave(tries.current, entry, true, newId);
      setNote({ ok: true, text: `${what}.`, entry: kept.id });
      const now = localNow(new Date(), org.time_zone);
      setForm((before) => blankForm(now.day, now.time, before.driverId, before.truckId));
      setTimeSet(false);
      setTarget(null);
      setNearby([]);
      const next = await user
        .getIdToken()
        .then(api.visitDefaults)
        .catch(() => (loaded ? keptVisitDefaults(scope, loads.day, loaded) : null));
      if (!live.current) return;
      if (next) setDefaults(next);
      if (next) await startFrom(next, entry.account_id);
      else chooseNearest();
    } catch (e) {
      if (!live.current) return;
      tries.current = afterSave(tries.current, entry, false, newId);
      setError({ field: "save", text: e instanceof Error && e.message === NO_STORAGE ? NO_STORAGE : saveFailure(e, "The visit").text });
    } finally {
      if (live.current) setBusy(false);
    }
  };

  const retry = () => {
    const pending = tries.current.entry;
    if (pending) void send(pending, Boolean(pending.old_confirmed));
  };

  const fieldError = (field: RefusedField | "save") => {
    if (error?.field !== field) return null;
    const preTrip = field === "save" && error.text === PRE_TRIP;
    return (
      <p id="visit-error" role="alert" className={preTrip ? "visit-pre-trip" : "error"} data-testid="visit-error">
        {preTrip && <span aria-hidden="true">⚠ </span>}
        {error.text}
        {preTrip ? (
          <>
            {" "}
            <a href={`${base}/today`} data-testid="visit-open-today" onClick={go}>
              Open Route
            </a>
          </>
        ) : field === "save" && tries.current.entry && (
          <>
            {" "}
            <button type="button" data-testid="visit-retry" disabled={busy} onClick={retry}>
              Retry
            </button>
          </>
        )}
      </p>
    );
  };
  const invalid = (field: RefusedField) => (error?.field === field ? { "aria-invalid": true, "aria-describedby": "visit-error" } : {});

  const drivers = loaded?.drivers ?? [];
  const trucks = loaded?.trucks ?? [];
  const you = loaded?.driver_id ?? null;
  const driverOptions = driverChoices(drivers, you);
  const driverName = driverOptions.find((option) => option.id === form.driverId)?.label ?? "–";
  const driverFixed = driverOptions.length <= 1 && error?.field !== "driver";
  const truckOptions = truckChoices(trucks, loaded?.truck_id ?? null);
  const shown = query.trim() && accounts ? accounts.filter((account) => matchesQuery(account, query)).slice(0, SEARCH_MAX) : [];
  const unit = volume.label;
  const noteEntry = note?.entry ? queued?.find((entry) => entry.id === note.entry) : undefined;
  const noteText = !note
    ? null
    : !note.entry
      ? note.text
      : noteEntry?.state === "failed"
        ? `Not saved: ${note.text} ${noteEntry.reason ?? ""}`.trim()
        : `${STATE_TEXT[noteEntry?.state === "sent" ? "sent" : "saved"]}: ${note.text}`;
  const refused = refusedVisits(queued, [note?.entry, fixing]);
  const failedEntry = noteEntry?.state === "failed" ? noteEntry : !note ? refused?.newest : undefined;
  const statusText = noteText === null ? (refused ? refusedText(refused) : null) : refused ? `${noteText} ${moreRefusedText(refused.count)}` : noteText;
  const offersChange = failedEntry !== undefined && fixing !== failedEntry.id && canChange(failedEntry.reason);
  const oldDate = timeSet && /^\d{4}-\d{2}-\d{2}$/.test(form.day) && /^\d{2}:\d{2}$/.test(form.time) && isOldEntry(zonedIso(form.day, form.time, org.time_zone), new Date(), hours);
  const startWaits = queued?.some((entry) => entry.kind === "start" && waiting(entry)) ?? false;

  return (
    <main className="visit-page form-page" data-testid="visit-page">
      <h1 tabIndex={-1}>Record a visit</h1>
      <InstallNote />
      {defaults === undefined ? (
        <Skeleton shape="form" />
      ) : typeof defaults === "string" ? (
        <Problem found={defaults} auth={auth} retry={() => setAttempt((n) => n + 1)} />
      ) : (
        <>
          {offlineAt !== null && checking && (
            <p className="offline-note" role="status" data-testid="visit-checking">
              {checkingText(offlineAt, org.time_zone)}
            </p>
          )}
          {offlineAt !== null && !checking && (
            <p className="offline-note" data-testid="visit-offline">
              {offlineText(offlineAt, org.time_zone)}.{" "}
              <button type="button" onClick={() => setAttempt((n) => n + 1)}>
                Try again
              </button>
            </p>
          )}
          <p
            role="status"
            data-testid="visit-status"
            data-refused={refused?.count}
            className={(note && (!note.ok || noteEntry?.state === "failed")) || (!note && refused) ? "error" : "visit-saved"}
          >
            {statusText}
            {(failedEntry || refused) && (
              <>
                {" "}
                {offersChange && (
                  <>
                    <button id="visit-fix" type="button" data-testid="visit-fix" aria-describedby="visit-fix-hint" onClick={() => void openFix(failedEntry)}>
                      {CHANGE_LABEL}
                    </button>{" "}
                  </>
                )}
                <a href={`${base}/sync`} onClick={go}>
                  Open Sync
                </a>
              </>
            )}
          </p>
          {offersChange && !askDraft && (
            <p id="visit-fix-hint" className="sub" data-testid="visit-fix-hint">
              {CHANGE_HINT}
            </p>
          )}
          {askDraft && (
            <p
              className="confirm"
              role="group"
              aria-label="Change the refused entry"
              data-testid="visit-draft-confirm"
              onKeyDown={(e) => {
                if (e.key === "Escape") keepValues();
              }}
            >
              <span aria-hidden="true">⚠ </span>
              {DRAFT_ASK}{" "}
              <button type="button" data-testid="visit-draft-change" onClick={() => void openFix(askDraft, true)}>
                {CHANGE_LABEL}
              </button>{" "}
              <button id="visit-draft-keep" type="button" className="primary" data-testid="visit-draft-keep" onClick={keepValues}>
                {DRAFT_KEEP}
              </button>
            </p>
          )}
          {defaults.pre_trip_needed && !startWaits && (
            <p className="visit-pre-trip" data-testid="visit-pre-trip">
              <span aria-hidden="true">⚠ </span>
              {PRE_TRIP}{" "}
              <a href={`${base}/today`} onClick={go}>
                Open Route
              </a>
            </p>
          )}
          <section className="visit-account" aria-labelledby="visit-account-title" data-testid="visit-account">
            <h2 id="visit-account-title" className="visually-hidden">
              Account
            </h2>
            {target ? (
              <div
                className={error?.field === "account" ? "visit-card invalid" : "visit-card"}
                data-testid="visit-target"
                data-account={target.number}
                data-invalid={error?.field === "account" || undefined}
              >
                <strong>{target.number}</strong> {target.name}
                {target.address && <span className="sub">{target.address}</span>}
                {target.routeStopId ? (
                  <span className="sub">Next stop of your route</span>
                ) : target.distance_m != null ? (
                  <span className="sub">{distanceText(target.distance_m)} from you</span>
                ) : null}
                <button
                  id="visit-change-account"
                  type="button"
                  className="link"
                  data-testid="visit-change-account"
                  aria-describedby={error?.field === "account" ? "visit-error" : undefined}
                  onClick={() => void searchAccounts()}
                >
                  Change
                </button>
                {fieldError("account")}
              </div>
            ) : finding === "stop" || finding === "gps" ? (
              <p data-testid="visit-finding">{finding === "stop" ? "Loading the next stop…" : "Finding the accounts near you…"}</p>
            ) : null}
            {nearby.length > 1 && finding !== "search" && (
              <ul className="visit-nearby" data-testid="visit-nearby">
                {nearby.slice(0, 5).map((account) => (
                  <li key={account.id}>
                    <button
                      type="button"
                      className="link"
                      aria-pressed={target?.id === account.id}
                      onClick={() => pick({ ...account, routeStopId: null })}
                    >
                      {account.number} {account.name} · {distanceText(account.distance_m)}
                    </button>
                  </li>
                ))}
              </ul>
            )}
            {(finding === "search" || !target) && finding !== "stop" && finding !== "gps" && (
              <label className="visit-search">
                Find an account by number or name
                <input
                  type="search"
                  data-testid="visit-search"
                  value={query}
                  autoComplete="off"
                  placeholder="D-0042 or name"
                  onFocus={() => void searchAccounts()}
                  onChange={(e) => setQuery(e.target.value)}
                />
              </label>
            )}
            {shown.length > 0 && (
              <ul className="visit-results" data-testid="visit-results">
                {shown.map((account) => (
                  <li key={account.id}>
                    <button
                      type="button"
                      className="link"
                      data-testid="visit-result"
                      onClick={() => pick({ id: account.id, number: account.number, name: account.name, address: account.address, routeStopId: null })}
                    >
                      {account.number} {account.name}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </section>
          {target && (
            <section className="visit-photo" aria-labelledby="visit-photo-title" data-testid="visit-photo">
              <h2 id="visit-photo-title" className="visually-hidden">
                Photos
              </h2>
              <p className="sub" data-testid="visit-photos">
                Photos on this phone: {localPhotos(queued ?? [], target.id, target.routeStopId ?? null).length}
              </p>
              <PhotoAdd
                key={`${target.id} ${target.routeStopId ?? ""}`}
                accountId={target.id}
                stopId={target.routeStopId ?? undefined}
                user={user}
                auth={auth}
                testId="visit-photo-add"
                keep={{ scope, routeId: null, what: `${target.number} ${target.name}`, all: queued ?? [] }}
                orderHref={(id) => `${base}/accounts/${target.id}?photo=${id}`}
              />
            </section>
          )}
          <form className="visit-form" data-testid="visit-form" noValidate onSubmit={(e) => void save(e)}>
            {!form.nothing && (
              <>
                <GallonsFields
                  id="visit"
                  testId="visit"
                  word={volume.word}
                  total={form.gallons}
                  scrap={form.scrap}
                  onTotal={(gallons) => change({ gallons })}
                  onScrap={(scrap) => change({ scrap })}
                  totalRef={gallonsRef}
                  totalAria={invalid("gallons")}
                  scrapAria={invalid("scrap")}
                >
                  {fieldError("gallons")}
                  {fieldError("scrap")}
                </GallonsFields>
              </>
            )}
            <div className="visit-fields" data-testid="visit-changes">
              <div className="visit-field">
                <label htmlFor="visit-when">Time:</label>
                <div className="visit-field-control">
                  <select id="visit-when" data-testid="visit-when" value={timeSet ? "set" : "now"} onChange={(e) => pickWhen(e.target.value === "set")}>
                    <option value="now">Now</option>
                    <option value="set">Other time</option>
                  </select>
                  {timeSet && (
                    <>
                      <input
                        id="visit-day"
                        type="date"
                        aria-label="Date"
                        data-testid="visit-day"
                        value={form.day}
                        aria-invalid={error?.field === "time" || undefined}
                        aria-describedby={[error?.field === "time" && "visit-error", oldDate && "visit-old-date"].filter(Boolean).join(" ") || undefined}
                        onChange={(e) => change({ day: e.target.value })}
                      />
                      <input
                        id="visit-time"
                        type="time"
                        aria-label="Time"
                        data-testid="visit-time"
                        value={form.time}
                        {...invalid("time")}
                        onChange={(e) => change({ time: e.target.value })}
                      />
                    </>
                  )}
                </div>
              </div>
              <div className="visit-field">
                <label htmlFor="visit-truck">Truck:</label>
                <div className="visit-field-control">
                  <select id="visit-truck" data-testid="visit-truck" value={form.truckId} {...invalid("truck")} onChange={(e) => change({ truckId: e.target.value })}>
                    {truckOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="visit-field">
                {driverFixed ? (
                  <>
                    <span className="visit-field-label">Driver:</span>
                    <span className="visit-field-control" data-testid="visit-driver-text">
                      {driverName}
                    </span>
                  </>
                ) : (
                  <>
                    <label htmlFor="visit-driver">Driver:</label>
                    <div className="visit-field-control">
                      <select id="visit-driver" data-testid="visit-driver" value={form.driverId} {...invalid("driver")} onChange={(e) => change({ driverId: e.target.value })}>
                        {driverOptions.map((option) => (
                          <option key={option.id} value={option.id}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </div>
                  </>
                )}
              </div>
            </div>
            {fieldError("time")}
            {oldDate && (
              <p id="visit-old-date" className="visit-pre-trip" data-testid="visit-old-date">
                <span aria-hidden="true">⚠ </span>
                {oldEntryText(hours)}
              </p>
            )}
            {fieldError("truck")}
            {fieldError("driver")}
            {labelList && labelList.labels.some((label) => label.active) && (
              <VisitLabelPicker list={labelList} chosen={form.labels} onChange={(labels) => change({ labels })} />
            )}
            <VisitNote value={form.note} onChange={(note) => change({ note })} error={error?.field === "note" ? error.text : null} />
            <button ref={saveRef} type="submit" className="primary visit-save" data-testid="visit-save" disabled={!target || busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            {askNoScrap && <NoScrapConfirm id="visit" testId="visit" disabled={busy} onYes={noScrapYes} onChange={changeScrap} />}
            {askOld && (
              <p
                className="confirm"
                role="group"
                aria-label="Save an old entry"
                data-testid="visit-old-confirm"
                onKeyDown={(e) => {
                  if (e.key !== "Escape") return;
                  setAskOld(false);
                  saveRef.current?.focus();
                }}
              >
                <span aria-hidden="true">⚠ </span>
                {oldEntryText(hours)}{" "}
                <button id="visit-old-yes" type="button" className="primary" data-testid="visit-old-yes" disabled={busy} onClick={() => void save(undefined, true)}>
                  Save the old entry
                </button>{" "}
                <button type="button" data-testid="visit-old-no" disabled={busy} onClick={changeOldDate}>
                  Change the date
                </button>
              </p>
            )}
            {askPlace && (
              <PlaceConfirm
                id="visit"
                place={askPlace.place}
                farM={loaded?.place_far_m ?? FAR_M}
                hint={askPlace.hint}
                disabled={busy}
                onYes={() => placeYes(askPlace)}
                onNo={() => placeNo(askPlace)}
              />
            )}
            {fieldError("save")}
            <div className="visit-toggles">
              {!form.nothing && (
                <label className="check">
                  <input type="checkbox" data-testid="visit-not-empty" checked={form.notEmpty} onChange={(e) => change({ notEmpty: e.target.checked })} />
                  Not empty
                </label>
              )}
              <button
                type="button"
                data-testid="visit-nothing"
                aria-pressed={form.nothing !== null}
                onClick={() => change({ nothing: form.nothing ? null : "not_full", left: "" })}
              >
                {form.nothing ? "Took gallons" : "Took nothing"}
              </button>
            </div>
            {form.nothing && (
              <fieldset id="visit-reason" tabIndex={-1} className="visit-reasons" data-testid="visit-reasons" {...invalid("reason")}>
                <legend>Why nothing?</legend>
                {NOTHING_REASONS.map((reason) => (
                  <button
                    key={reason}
                    type="button"
                    className="chip-choice"
                    data-testid={`visit-reason-${reason}`}
                    aria-pressed={form.nothing === reason}
                    onClick={() => change({ nothing: reason })}
                  >
                    {REASON_TEXT[reason]}
                  </button>
                ))}
              </fieldset>
            )}
            {form.nothing && fieldError("reason")}
            {asksLeft(form) && (
              <>
                <label>
                  {`Level left (${unit})`}
                  <input
                    id="visit-left"
                    data-testid="visit-left"
                    inputMode="decimal"
                    autoComplete="off"
                    value={form.left}
                    {...invalid("left")}
                    onChange={(e) => change({ left: e.target.value })}
                  />
                </label>
                {fieldError("left")}
              </>
            )}
          </form>
        </>
      )}
    </main>
  );
}
